import mixpanel from "mixpanel-browser";
import { defineStore, storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { ref } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { type types, useServices } from "@/lib/openapi";
// Import enum directly as index only exports as types.
import { ClusterRole as ClusterRoleEnum } from "@/lib/openapi/gen/types.gen";

import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "./authentication-store";

export type MeUser = types.MeUser;
export type ClusterRole = types.ClusterRole;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const ClusterRole = ClusterRoleEnum;

export const useUserStore = defineStore("user", () => {
    const { organization } = useOrganizationManager();
    const { signOut } = useAuthenticationStore();
    const { authStatus, authUser } = useAuthenticationManager();
    const router = useRouter();
    const user = ref<MeUser>();
    const currentOrganizationRole = ref<ClusterRole>();
    const controller = ref(new AbortController());

    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const updatePhotoUrlFromAuthData = async () => {
        try {
            await api.updateSelfUserMePut({
                body: {
                    profile_picture: authUser.value.photoUrl,
                },
            });
        } catch (error) {
            console.error(
                "Failed to update profile picture in backend:",
                error
            );
        }
    };

    async function updateUser() {
        controller.value.abort();
        controller.value = new AbortController();

        try {
            const { response, data } = await api.getSelfUserMeGet({
                signal: controller.value.signal,
            });
            if (!response?.ok) {
                if (response.status === 401) {
                    router.push({ name: "home" });
                    await signOut({
                        severity: "error",
                        summary: "Incorrect login.",
                    });
                    reset();
                } else {
                    throw new Error(response.statusText);
                }
            } else {
                user.value = data;

                if (
                    authUser.value?.photoUrl &&
                    authUser.value.photoUrl !== user.value?.profile_picture
                ) {
                    updatePhotoUrlFromAuthData();
                }
                for (const [orgId, role] of Object.entries(user.value!.roles)) {
                    if (orgId === organization.value?.identity) {
                        currentOrganizationRole.value = role;
                        break;
                    }
                }
                mixpanel.identify(user.value?.identity);
                mixpanel.people.set_once({
                    "role": currentOrganizationRole.value,
                });
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        }
    }

    function reset() {
        user.value = undefined;
    }

    // When the organization changes, the permissions for the current user
    // change as well.
    watch(
        () => organization.value?.identity,
        () => {
            updateUser();
        }
    );

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                reset();
            } else {
                updateUser();
            }
        },
        { immediate: true }
    );

    return {
        user,
        currentOrganizationRole,
        reset,
        photoUrl: computed(() => user.value?.profile_picture || ""),
    };
});

export const useUserManager = () => storeToRefs(useUserStore());
